import React from 'react';
import useStores from '@stores/useStores';
import classes from '@components/sources/visualizer/mappingScreens/config/mappingConfig/mapping-commons.module.scss';
import { reloadButton } from '@components/sources/visualizer/mappingScreens/config/mappingConfig/SelectUniqueIdentifier';
import {
  fetchErrorAssets,
  ErrorProps,
} from '@components/sources/visualizer/mappingScreens/errorsView';
import { getApiErrorMessage } from '@components/common/util/util';
import SingleSelect from '@ui-library/formGroup/singleSelect';
import Button from '@ui-library/button';
import useModalView from '@hooks/useModalView';
import Modal from '@ui-library/modal/custom';
import { IModelStore } from '@stores/model';
import { Configuration } from '@components/models/addModel/steps/configuration';
import { sortBy } from 'lodash';
import { ConfigEditorProps, IColumn } from '.';

export interface ModelConfig {
  sqlModelId?: string;
  accountId?: string;
}

interface ConfigureSqlModelProps extends ConfigEditorProps<ModelConfig> {
  role: string;
  updateSchemaErrors: (errorItems: ErrorProps[]) => void;
}

const ModelSelector: React.FC<ConfigureSqlModelProps> = ({
  defaultValue,
  onChangeConfig,
  onChangeColumns,
  role,
  onChangeLoadingState,
  isViewOrUpdate,
  mappedToDestination,
  updateSchemaErrors,
}: ConfigureSqlModelProps) => {
  const { messagesStore, modelListStore, sourceDataStore } = useStores();

  const [loadingCols, setLoadingCols] = React.useState(false);
  const [model, setModel] = React.useState<IModelStore | undefined>(
    modelListStore.getModelById(defaultValue?.sqlModelId || ''),
  );
  const [refresh, setRefresh] = React.useState(false);
  React.useEffect(() => {
    if (onChangeLoadingState) {
      onChangeLoadingState(loadingCols);
    }
  }, [loadingCols, refresh]);

  const startReload = async () => {
    try {
      setRefresh(true);
      updateSchemaErrors([fetchErrorAssets('models')]);
      await modelListStore.getModels(true);
    } catch (err) {
      const errMsg = getApiErrorMessage(err, 'Failed to reload');
      updateSchemaErrors([fetchErrorAssets('models', errMsg)]);
      messagesStore.showErrorMessage(errMsg);
    } finally {
      setRefresh(false);
    }
  };
  const refreshModelsButton = () => (
    <div className={classes.dropdown_reload_button_wrapper}>
      {reloadButton({ type: 'text', refreshing: refresh, onClick: startReload }, 'models')}
    </div>
  );
  const onModelSelect = async (_: unknown, { value }: { value: string }) => {
    if (!value) {
      return;
    }
    try {
      const selectedSqlModel = modelListStore.getModelById(value);
      setModel(selectedSqlModel);
      onChangeConfig({ sqlModelId: value, accountId: selectedSqlModel?.account.id });

      setLoadingCols(true);

      if (isViewOrUpdate && mappedToDestination) {
        return;
      }
      updateSchemaErrors([fetchErrorAssets('columns')]);
      const cols = await sourceDataStore.getColumns(
        { sqlModelId: value, accountId: selectedSqlModel?.account.id },
        role,
      );
      onChangeColumns(mappedToDestination ? cols : cols.map((col: IColumn) => col.name));
    } catch (err) {
      const errorMsg = getApiErrorMessage(err, 'Failed to fetch columns');
      messagesStore.showErrorMessage(errorMsg);
      updateSchemaErrors([fetchErrorAssets('columns', errorMsg)]);
      onChangeColumns([]);
    } finally {
      setLoadingCols(false);
    }
  };
  const configuredData = (
    <div className={classes.modal__wrapper}>
      <Configuration isViewMode settings={{ config: model?.config }} insideModal />
    </div>
  );
  const [modelConfig, showModelConfig] = useModalView(
    <Modal
      size="xl"
      title="Model configuration"
      onClose={() => {
        showModelConfig(false);
      }}
      content={configuredData}
      saveLabel="Ok"
      onSave={() => {
        showModelConfig(false);
      }}
      showClose={false}
    />,
  );
  return (
    <div className={`sql_container ${!mappedToDestination ? classes.json_mapper : ''}`}>
      {modelConfig}
      <div className={`${classes.select} ${model?.id ? classes.details : ''}`}>
        <SingleSelect
          field={{
            placeholder: 'Search for Model name',
            showSearch: true,
            label: 'Model',
            required: true,
            default: { value: model?.id, label: model?.name },
            options: sortBy(
              modelListStore.models
                .filter(({ sourceDefinition: { name } }) => name === role)
                .map((opt) => ({ name: opt.name, value: opt.id })),
              'name',
            ),
            loading: loadingCols,
            labelInValue: true,
            optionLabelProp: 'label',
            optionFilterProp: 'label',
          }}
          disabled={refresh || loadingCols || isViewOrUpdate}
          onChange={onModelSelect}
        />
        {model?.id && (
          <span className={`${classes.model_details}`}>
            <Button
              name="Details"
              textPrimary
              onClick={() => {
                showModelConfig(true);
              }}
            />
          </span>
        )}
      </div>
      {!isViewOrUpdate && refreshModelsButton()}
    </div>
  );
};

export default ModelSelector;
